import {
  AppScopeIdentity,
  AppScopeIdentityAt,
  appScopeUtils
} from 'v5/core/app-scope/appScope.types';
import {
  decodeSettingValue,
  settingCatalog,
  SettingDefinedAt,
  SettingName,
  SettingValue,
  TeamSettingName,
  WorkspaceSettingName
} from 'v5/api/system-settings/settingCatalog';
import {
  ResolvedSetting,
  SystemSetting,
  SystemSettingOrigin
} from 'v5/api/system-settings/system-settings.api';
import { SystemSettingWireRow } from 'store/v1/auth/systemSettings.bridge';

export type SystemSettingsRows = SystemSetting[] | SystemSettingWireRow[];

/** The row a scope reads or writes: the organisation's (no ids), a workspace's or a team's. */
export type SettingLens = {
  enterpriseWorkspaceId: number | null;
  enterpriseTeamId: number | null;
};

const GLOBAL_LENS: SettingLens = {
  enterpriseWorkspaceId: null,
  enterpriseTeamId: null
};

/** The row a scope writes: its own level's, never a parent's. */
export function exactScopeOf(scope: AppScopeIdentity): SettingLens {
  return {
    enterpriseWorkspaceId:
      scope.level === 'organization' ? null : scope.workspaceId,
    enterpriseTeamId: scope.level === 'team' ? scope.teamId : null
  };
}

export function isSettingRowAt(
  row: SystemSettingWireRow | SystemSetting,
  name: string,
  lens: SettingLens
): boolean {
  return (
    row.name === name &&
    (row.enterprise_workspace_id ?? null) === lens.enterpriseWorkspaceId &&
    (row.enterprise_team_id ?? null) === lens.enterpriseTeamId
  );
}

const LEVEL_RANK: Record<AppScopeIdentity['level'], number> = {
  organization: 0,
  workspace: 1,
  team: 2
};

/**
 * The rows a scope reads a setting from, nearest first: the team's, the workspace's, then the
 * organisation's (global) row. A setting is never read below its defined level, so a workspace
 * key read from a team starts at the workspace.
 */
function lensChainFor(
  scope: AppScopeIdentity,
  definedAt: 'workspace' | 'team'
): SettingLens[] {
  if (scope.level === 'organization') {
    return [GLOBAL_LENS];
  }
  const workspace = exactScopeOf(appScopeUtils.lowerTo('workspace', scope));
  if (scope.level === 'workspace' || definedAt === 'workspace') {
    return [workspace, GLOBAL_LENS];
  }
  return [exactScopeOf(scope), workspace, GLOBAL_LENS];
}

/** A setting is stored at its defined level or above it: a team key per organisation, workspace or team. */
export function assertWriteScope<K extends SettingName>(
  scope: AppScopeIdentity,
  name: K
): asserts scope is WriteScopeFor<K> {
  const definedAt = settingCatalog[name].definedAt;
  if (LEVEL_RANK[scope.level] > LEVEL_RANK[definedAt]) {
    throw new Error(
      `Cannot write ${name} (definedAt: ${definedAt}) from ${scope.level} scope`
    );
  }
}

const originOf = (lens: SettingLens): SystemSettingOrigin =>
  lens.enterpriseTeamId != null
    ? 'team'
    : lens.enterpriseWorkspaceId != null
      ? 'workspace'
      : 'global';

export function resolveSettingValue<K extends SettingName>(
  rows: SystemSettingsRows,
  scope: AppScopeIdentity,
  name: K
): ResolvedSetting<SettingValue<K>> {
  for (const lens of lensChainFor(scope, settingCatalog[name].definedAt)) {
    const row = rows.find(row => isSettingRowAt(row, name, lens));
    if (row) {
      return {
        name,
        value: decodeSettingValue(name, row.value) as SettingValue<K>,
        origin: originOf(lens)
      };
    }
  }

  return {
    name,
    value: settingCatalog[name].default as SettingValue<K>,
    origin: 'default' satisfies SystemSettingOrigin
  };
}

export function resolveAllSettings(
  rows: SystemSettingsRows,
  scope: AppScopeIdentity
): Record<string, ResolvedSetting> {
  return Object.fromEntries(
    (Object.keys(settingCatalog) as SettingName[]).map(name => [
      name,
      resolveSettingValue(rows, scope, name)
    ])
  );
}

// over the whole union of keys: a patch mixing workspace and team keys is written at a workspace
type LevelsUpTo<L extends AppScopeIdentity['level']> = [L] extends ['team']
  ? AppScopeIdentity['level']
  : [L] extends ['workspace' | 'team']
    ? 'organization' | 'workspace'
    : 'organization';

/** The scopes a setting can be written from: its defined level and every level above it. */
export type WriteScopeFor<K extends SettingName> = AppScopeIdentityAt<
  LevelsUpTo<SettingDefinedAt<K>>
>;

export type { WorkspaceSettingName, TeamSettingName };
